<template>
  <div :class="containerClass">
    <page-header
      :title="this.$route.params.id ? 'Edit Assignment' : 'Add Assignment'"
      :container-class="null"
      :show-back="true"
      class="mb-32pt"
    />

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="handleSubmit">
      <!-- Title -->
      <b-form-group label="Title" label-for="assignment-title" label-class="form-label">
        <b-form-input id="assignment-title" v-model="formData.title" placeholder="Enter assignment title" required />
      </b-form-group>

      <!-- Description -->
      <b-form-group label="Description" label-for="assignment-description" label-class="form-label">
        <b-form-textarea
          id="assignment-description"
          v-model="formData.description"
          placeholder="Enter assignment description"
          rows="3"
          max-rows="6"
        />
      </b-form-group>

      <!-- Assignment Type -->
      <b-form-group label="Assignment Type" label-for="assignment-type" label-class="form-label">
        <b-form-radio-group
          id="assignment-type"
          v-model="formData.assignment_type"
          :options="assignmentTypeOptions"
          name="assignment-type"
          @change="handleAssignmentTypeChange"
        />
        <small class="form-text text-muted">
          Standard assignments hold checkbox, upload, text and case-study tasks. Scenario assignments hold only school
          scenario interviews, which complete automatically once the student finishes the call.
        </small>
      </b-form-group>

      <!-- Estimated Time -->
      <b-form-group label="Estimated Time (minutes)" label-for="assignment-estimated-time" label-class="form-label">
        <b-form-input
          id="assignment-estimated-time"
          v-model.number="formData.estimated_time_minutes"
          type="number"
          min="0"
          placeholder="Enter estimated time in minutes"
        />
        <small class="form-text text-muted">
          Leave this field blank if you want to calculate the time based on the sum of lessons and lectures
        </small>
      </b-form-group>

      <!-- Max Attempts -->
      <b-form-group label="Max Attempts" label-for="assignment-max-attempts" label-class="form-label">
        <b-form-input
          id="assignment-max-attempts"
          v-model.number="formData.max_attempts"
          type="number"
          min="0"
          placeholder="Enter maximum attempts (0 for unlimited)"
        />
        <small class="form-text text-muted">Set to 0 to allow unlimited attempts</small>
      </b-form-group>

      <!-- Program -->
      <b-form-group label="Program" label-for="assignment-program" label-class="form-label">
        <v-select
          id="assignment-program"
          class="form-control v-select-custom"
          label="title"
          v-model="formData.program"
          :reduce="program => program.id"
          placeholder="Select Program"
          :options="allPrograms"
          :loading="areProgramsLoading"
        >
        </v-select>
      </b-form-group>

      <!-- Scenario Interview (scenario assignments only) -->
      <div v-if="isScenarioAssignment" class="card mb-4">
        <div class="card-header">
          <h5 class="mb-0">Scenario Interview</h5>
        </div>
        <div class="card-body">
          <b-form-group label="School Scenario Interview" label-for="assignment-simulation" label-class="form-label">
            <v-select
              id="assignment-simulation"
              class="form-control v-select-custom"
              label="title"
              v-model="formData.simulation"
              :reduce="option => option.id"
              placeholder="Select a school scenario interview"
              :options="scenarioInterviews"
              :loading="areInterviewsLoading"
            >
              <template #search="{ attributes, events }">
                <input class="vs__search" v-bind="attributes" v-on="events" />
              </template>
              <template slot="option" slot-scope="option">
                <div>
                  <span>{{ option.title }}</span>
                  <small v-if="interviewCourses(option)" class="d-block text-muted">
                    {{ interviewCourses(option) }}
                  </small>
                </div>
              </template>
            </v-select>
            <small class="form-text text-muted">
              A scenario assignment is a single interview &mdash; its task is generated automatically from the details
              above and completes once the student finishes the call.
            </small>
            <b-alert
              v-if="!areInterviewsLoading && !scenarioInterviews.length"
              show
              variant="warning"
              class="mt-2 mb-0"
            >
              No school scenario interviews are available for this school and program yet. A super admin creates them under
              Interviews.
            </b-alert>
          </b-form-group>

          <b-form-group
            v-if="formData.is_graded"
            label="Grading Rubric"
            label-for="assignment-scenario-rubric"
            label-class="form-label"
          >
            <b-form-textarea
              id="assignment-scenario-rubric"
              v-model="scenarioRubric"
              placeholder="What the interview is graded on"
              rows="3"
              max-rows="8"
            />
            <small class="form-text text-muted">
              Used when grading the interview transcript. Carries the assignment's total marks.
            </small>
          </b-form-group>
        </div>
      </div>

      <!-- Grading Configuration -->
      <div class="card mb-4">
        <div class="card-header">
          <h5 class="mb-0">Grading Configuration</h5>
        </div>
        <div class="card-body">
          <!-- Is Graded -->
          <b-form-group label-class="form-label">
            <b-form-checkbox v-model="formData.is_graded" switch>
              This is a graded assignment
            </b-form-checkbox>
            <small class="form-text text-muted">
              Enable manual grading for this assignment. Students will be notified once graded.
            </small>
          </b-form-group>

          <!-- Total Marks -->
          <b-form-group v-if="formData.is_graded" label="Total Marks" label-for="total-marks" label-class="form-label">
            <b-form-input
              id="total-marks"
              v-model.number="formData.total_marks"
              type="number"
              min="0"
              placeholder="Enter total marks"
              required
            />
            <small class="form-text text-muted">
              Maximum marks that can be awarded for this assignment.
            </small>
          </b-form-group>

          <!-- Weightage -->
          <b-form-group v-if="formData.is_graded" label="Weightage (%)" label-for="weightage" label-class="form-label">
            <b-form-input
              id="weightage"
              v-model.number="formData.weightage"
              type="number"
              min="0"
              max="100"
              step="0.01"
              placeholder="Enter weightage percentage"
            />
            <small class="form-text text-muted">
              The weightage of this assignment in the overall course grade.
            </small>
          </b-form-group>
        </div>
      </div>

      <!-- AI Auto-Grading Section -->
      <assignment-ai-section v-if="isSchoolAiGradingEnabled" v-model="aiSection" :is-graded="formData.is_graded" />

      <!-- Tasks Section -->
      <div
        v-if="formData.is_graded && !isScenarioAssignment"
        class="d-flex align-items-center mb-2"
        :class="marksIndicatorClass"
      >
        <i class="material-icons icon-16pt mr-1">functions</i>
        <strong class="mr-2">Marks allocated:</strong>
        {{ allocatedMarks }} / {{ formData.total_marks || 0 }}
        <span v-if="marksDelta !== 0" class="ml-2">
          ({{ marksDelta > 0 ? `+${marksDelta} over` : `${Math.abs(marksDelta)} unassigned` }})
        </span>
      </div>

      <b-button
        v-if="!isScenarioAssignment"
        variant="outline-primary"
        class="mr-2"
        @click="openTaskModal"
        style="justify-self: end;display: block;"
      >
        <i class="fas fa-plus mr-1"></i> Add Task
      </b-button>

      <!-- Lessons Section -->
      <div v-if="!isScenarioAssignment" class="form-group mb-4">
        <label class="form-label">Tasks</label>
        <draggable
          v-if="tasks && tasks.length > 0"
          v-model="tasks"
          class="mb-3"
          handle=".drag-handle"
          @end="updateTaskOrder"
        >
          <div
            v-for="(task, index) in tasks"
            :key="task.id || index"
            class="d-flex justify-content-between align-items-center"
          >
            <div class="drag-handle" style="cursor: move;">
              <i class="fas fa-bars icon-16pt mr-2"></i>
            </div>
            <div class="card mb-2" style="width: 100%">
              <div class="card-body p-3 d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="mb-0" style="font-size: medium;">{{ task.title }}</h6>
                  <div v-if="task.completion_mode === 'scenario'" class="task-meta text-muted small mt-1">
                    <i class="material-icons icon-12pt">record_voice_over</i>
                    {{ taskInterviewTitle(task) || 'No interview selected' }}
                  </div>
                  <div v-if="formData.is_graded" class="task-meta text-muted small mt-1">
                    <span
                      >{{ Number(task.task_marks || 0) }} mark<span v-if="Number(task.task_marks || 0) !== 1"
                        >s</span
                      ></span
                    >
                    <span v-if="shouldShowRubricWarning(task)" class="text-warning ml-2">
                      <i class="material-icons icon-12pt">warning</i> no rubric
                    </span>
                  </div>
                </div>
                <div>
                  <a
                    @click="editTask(index)"
                    class="svg-icon mr-2"
                    v-b-popover.hover.left
                    :title="$t('generalMsgs.edit')"
                  >
                    <md-icon class="text-info">edit</md-icon>
                  </a>

                  <a
                    href="#"
                    @click.prevent="removeTask(index)"
                    v-b-popover.hover.left
                    :title="$t('generalMsgs.remove')"
                  >
                    <md-icon class="text-danger">delete</md-icon>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </draggable>
        <b-alert v-else show variant="info" class="mb-3">
          No tasks added yet. Click 'Add Task' to create one.
        </b-alert>
      </div>
      <b-btn variant="primary" :disabled="isSubmitting" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isSubmitting" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? 'Update' : 'Create' }}</span>
      </b-btn>
    </b-form>

    <!-- Task Modal -->
    <TaskModal
      v-if="!isScenarioAssignment"
      ref="taskModal"
      :modal-id="taskModalId"
      :task="currentTask"
      :is-editing="isEditingTask"
      :is-graded-assignment="formData.is_graded"
      :is-ai-grading-enabled="isSchoolAiGradingEnabled && aiSection.auto_grade_enabled"
      :assignment-id="$route.params.id || null"
      :assignment-type="formData.assignment_type"
      :program-id="formData.program"
      @submit="handleTaskSubmit"
    />
  </div>
</template>

<script>
import PageHeader from '@/components/Ui/PageHeader.vue';
import draggable from 'vuedraggable';
import { get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import Page from '../../../components/Page.vue';
import TaskModal from './components/TaskModal.vue';
import AssignmentAiSection, { AI_ASSIGNMENT_DEFAULTS } from '@/components/AiGrading/AssignmentAiSection.vue';

export default {
  name: 'PostAssignments',
  components: {
    TaskModal,
    PageHeader,
    draggable,
    AssignmentAiSection,
  },
  extends: Page,

  data() {
    return {
      formData: {
        title: '',
        description: '',
        assignment_type: 'standard',
        simulation: null,
        estimated_time_minutes: null,
        program: null,
        is_graded: false,
        total_marks: 0,
        weightage: 0,
        max_attempts: 0,
      },
      aiSection: { ...AI_ASSIGNMENT_DEFAULTS },
      scenarioRubric: '',
      scenarioInterviews: [],
      areInterviewsLoading: false,
      tasks: [],
      currentTask: null,
      currentTaskIndex: -1,
      isEditingTask: false,
      isSubmitting: false,
      taskModalId: 'task-modal',
      isEditing: false,
      allPrograms: [],
      areProgramsLoading: false,
      assignmentTypeOptions: [
        { text: 'Standard', value: 'standard' },
        { text: 'Scenario (interviews)', value: 'scenario' },
      ],
    };
  },
  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    isScenarioAssignment() {
      return this.formData.assignment_type === 'scenario';
    },
    isSchoolAiGradingEnabled() {
      return !!get(this.getLoggedInUser, 'linked_entity.ai_grading_enabled');
    },
    allocatedMarks() {
      return this.tasks.reduce((acc, t) => acc + (Number(t.task_marks) || 0), 0);
    },
    marksDelta() {
      return this.allocatedMarks - (Number(this.formData.total_marks) || 0);
    },
    marksIndicatorClass() {
      if (this.marksDelta > 0) return 'text-danger';
      if (this.marksDelta < 0) return 'text-warning';
      return 'text-success';
    },
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Assignments', to: { name: 'list-assignments' } },
        {
          text: this.$route.params.id ? 'Edit Assignment' : 'Add Assignment',
          active: true,
        },
      ];
    },
  },

  watch: {
    'formData.assignment_type'(value) {
      if (value === 'scenario') this.fetchScenarioInterviews();
    },
    'formData.program'() {
      // Changing the program changes which interviews are on offer, so the
      // list is reloaded and any selection that no longer belongs to the
      // program is dropped rather than left behind.
      if (this.isScenarioAssignment) this.fetchScenarioInterviews({ dropStaleSelection: true });
    },
  },

  methods: {
    ...mapActions('assignments', ['createAssignment', 'updateAssignment', 'getAssignment']),
    ...mapActions('simulations', ['getSchoolScenarios']),

    async fetchScenarioInterviews({ dropStaleSelection = false } = {}) {
      // Same scoping the task picker used: the school is pinned server-side for
      // school roles, and the program narrows the list.
      this.areInterviewsLoading = true;
      try {
        const params = {};
        if (this.formData.program) params.program = this.formData.program;
        const res = await this.getSchoolScenarios(params);
        this.scenarioInterviews = res.data.results || res.data;
      } catch (e) {
        this.scenarioInterviews = [];
      }
      this.areInterviewsLoading = false;

      // The interview must belong to the selected program. Anything left over
      // from a previous program would be rejected on save, so clear it here
      // and say why instead of failing at the end.
      if (dropStaleSelection && this.formData.simulation) {
        const stillOffered = this.scenarioInterviews.some(item => item.id === this.formData.simulation);
        if (!stillOffered) {
          this.formData.simulation = null;
          this.makeToast({
            variant: 'warning',
            msg: 'The selected interview is not available for this program. Please pick another one.',
          });
        }
      }
    },

    interviewCourses(interview) {
      return (interview.courses || []).map(course => course.title).join(', ');
    },

    ...mapActions('program', ['getAllPrograms']),

    async fetchPrograms() {
      this.areProgramsLoading = true;
      try {
        const res = await this.getAllPrograms();
        this.allPrograms = res.data;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.areProgramsLoading = false;
    },

    async fetchAssignment() {
      try {
        const response = await this.getAssignment(this.$route.params.id);
        this.formData = {
          title: response.data.title,
          description: response.data.description,
          assignment_type: response.data.assignment_type || 'standard',
          simulation: response.data.simulation ? response.data.simulation.id : null,
          estimated_time_minutes: response.data.estimated_time_minutes,
          program: typeof response.data.program === 'object' ? response.data.program?.id : response.data.program,
          is_graded: response.data.is_graded || false,
          total_marks: response.data.total_marks || 0,
          weightage: response.data.weightage || 0,
          max_attempts: response.data.max_attempts || 0,
        };
        this.aiSection = {
          auto_grade_enabled: !!response.data.auto_grade_enabled,
          human_review_required: !!response.data.human_review_required,
          auto_grade_context: response.data.auto_grade_context || '',
          learning_objectives: response.data.learning_objectives || '',
        };
        this.scenarioRubric = response.data.scenario_rubric || '';
        if (this.isScenarioAssignment) await this.fetchScenarioInterviews();
        // The API nests the interview object; the form works with its id plus a
        // title for display, and sends the id back on save.
        this.tasks = (response.data.tasks || []).map(task => ({
          ...task,
          simulation: task.simulation ? task.simulation.id : null,
          simulation_title: task.simulation ? task.simulation.title : '',
        }));
      } catch (error) {
        console.error('Error fetching assignment:', error);
        this.makeToast({ variant: 'danger', msg: 'Failed to load assignment' });
      }
    },

    taskInterviewTitle(task) {
      // Freshly picked tasks carry the title inline; saved ones come back nested.
      return task.simulation_title || (task.simulation && task.simulation.title) || '';
    },
    shouldShowRubricWarning(task) {
      // §3.2: warn when AI is on and a non-checkbox task has no rubric.
      if (!this.isSchoolAiGradingEnabled || !this.aiSection.auto_grade_enabled) return false;
      if (task.completion_mode === 'checkbox') return false;
      return !task.rubric || !String(task.rubric).trim();
    },
    handleAssignmentTypeChange(newType) {
      // Task kinds do not carry across types, so ask before dropping them.
      if (!this.tasks.length) return;
      this.$bvModal
        .msgBoxConfirm(
          'Switching the assignment type removes the tasks already added, because scenario and standard assignments ' +
            'use different task kinds. Continue?',
          {
            title: 'Change Assignment Type',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Remove tasks',
            cancelTitle: 'Cancel',
            footerClass: 'p-2',
            centered: true,
          }
        )
        .then(confirmed => {
          if (confirmed) {
            this.tasks = [];
          } else {
            this.formData.assignment_type = newType === 'scenario' ? 'standard' : 'scenario';
          }
        });
    },
    openTaskModal(task = null, index = -1) {
      this.currentTask = task
        ? { ...task }
        : {
            title: '',
            description: '',
            url: '',
            completion_mode: this.isScenarioAssignment ? 'scenario' : 'checkbox',
            simulation: null,
            order: this.tasks.length,
            task_marks: 0,
            rubric: '',
          };
      this.currentTaskIndex = index;
      this.isEditingTask = index >= 0;
      this.$nextTick(() => {
        this.$bvModal.show(this.taskModalId);
      });
    },

    handleTaskSubmit(taskData) {
      if (this.isEditingTask && this.currentTaskIndex >= 0) {
        // Update existing task
        this.$set(this.tasks, this.currentTaskIndex, {
          ...this.tasks[this.currentTaskIndex],
          ...taskData,
        });
      } else {
        // Add new task
        this.tasks.push({
          ...taskData,
          id: Date.now().toString(), // Temporary ID for frontend use
        });
      }
      this.$bvModal.hide(this.taskModalId);
    },
    updateTaskOrder() {
      // Update the order field of each task based on their new position
      this.tasks = this.tasks.map((task, index) => ({
        ...task,
        order: index + 1,
      }));
    },
    editTask(index) {
      if (index >= 0 && index < this.tasks.length) {
        this.openTaskModal(this.tasks[index], index);
      }
    },

    removeTask(index) {
      if (index >= 0 && index < this.tasks.length) {
        this.$bvModal
          .msgBoxConfirm('Are you sure you want to delete this task?', {
            title: 'Delete Task',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Delete',
            cancelTitle: 'Cancel',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          })
          .then(confirmed => {
            if (confirmed) {
              this.tasks.splice(index, 1);
            }
          });
      }
    },

    async handleSubmit() {
      if (this.isScenarioAssignment) {
        if (!this.formData.simulation) {
          this.makeToast({ variant: 'danger', msg: 'Please select a school scenario interview' });
          return;
        }
      } else if (this.tasks.length === 0) {
        this.makeToast({ variant: 'danger', msg: 'Please add at least one task' });
        return;
      }
      if (this.isSubmitting) return;

      this.isSubmitting = true;

      try {
        // Filter tasks: remove temporary IDs (numeric strings) for newly created tasks
        const processedTasks = this.tasks.map(task => {
          const taskCopy = { ...task };
          // Display-only field; not part of the Task payload.
          delete taskCopy.simulation_title;
          // Check if ID is a temporary one (numeric timestamp string)
          // Temporary IDs are created using Date.now().toString()
          if (taskCopy.id && /^\d+$/.test(taskCopy.id)) {
            // Remove the temporary ID so backend creates a new one
            delete taskCopy.id;
          }
          return taskCopy;
        });

        if (this.isSchoolAiGradingEnabled && this.aiSection.auto_grade_enabled && !this.formData.is_graded) {
          this.makeToast({ variant: 'danger', msg: 'AI grading can only be enabled on graded assignments.' });
          this.isSubmitting = false;
          return;
        }

        // A scenario assignment is one interview; its single task is generated
        // server-side from these fields, so no tasks are sent.
        const assignmentData = this.isScenarioAssignment
          ? {
              ...this.formData,
              ...(this.isSchoolAiGradingEnabled ? this.aiSection : AI_ASSIGNMENT_DEFAULTS),
              scenario_rubric: this.formData.is_graded ? this.scenarioRubric : '',
            }
          : {
              ...this.formData,
              ...(this.isSchoolAiGradingEnabled ? this.aiSection : AI_ASSIGNMENT_DEFAULTS),
              simulation: null,
              tasks: processedTasks,
            };

        if (this.isEditing) {
          await this.updateAssignment({
            id: this.$route.params.id,
            data: assignmentData,
          });
          this.makeToast('Assignment updated successfully', {
            title: 'Success',
            variant: 'success',
            solid: true,
          });
          this.$router.push({ name: 'list-assignments' });
        } else {
          await this.createAssignment(assignmentData);
          this.makeToast({ variant: 'success', msg: 'Assignment created successfully' });
          this.$router.push({ name: 'list-assignments' });
        }
      } catch (error) {
        console.error('Error saving assignment:', error);
        this.makeToast({ variant: 'danger', msg: 'Failed to save assignment' });
      } finally {
        this.isSubmitting = false;
      }
    },
  },
  async mounted() {
    await this.fetchPrograms();
    if (this.$route.params.id) {
      this.isEditing = true;
      await this.fetchAssignment();
    }
  },
  filters: {
    truncate(text, length, suffix = '...') {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + suffix : text;
    },
  },
};
</script>

<style scoped>
.assignment-form {
  max-width: 800px;
  margin: 0 auto;
}

.tasks-section {
  margin-top: 2rem;
  padding: 1rem;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
}
</style>
